import React from "react";
import { NavLink, Outlet, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import LanguageToggle from "@/components/LanguageToggle";
import { ShoppingBag, Package, Settings2, Users, LogOut, Wallet, Network, Calendar, BookOpen, User2, FileText, BarChart3, ClipboardList, UserCircle2, Tags } from "lucide-react";

export default function AdminLayout() {
  const { t } = useTranslation();
  const { user, loading, logout } = useAuth();

  if (loading) return <div className="p-16 text-[#4a6e77]">{t("common.loading")}</div>;
  if (!user) return <Navigate to="/login?next=/admin/orders" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const link = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
      isActive ? "bg-[#005870] text-white" : "text-[#003645] hover:bg-[#e5ebeb]/50"
    }`;

  return (
    <div className="min-h-screen bg-[#fdfbf7] flex">
      <aside className="hidden md:flex md:flex-col w-60 border-r border-[#e5ebeb] bg-white p-6 sticky top-0 h-screen">
        <Logo size="sm" />
        <div className="mt-2 text-[10px] uppercase tracking-widest text-[#00868e] font-semibold">
          {t("admin.title")}
        </div>
        <nav className="mt-8 flex-1 space-y-1 overflow-y-auto" data-testid="admin-nav">
          <NavLink to="/admin/analytics" className={link} data-testid="admin-nav-analytics">
            <BarChart3 size={16} /> {t("adminP3.analytics")}
          </NavLink>
          <NavLink to="/admin/orders" className={link} data-testid="admin-nav-orders">
            <ShoppingBag size={16} /> {t("admin.orders")}
          </NavLink>
          <NavLink to="/admin/products" className={link} data-testid="admin-nav-products">
            <Package size={16} /> {t("admin.products")}
          </NavLink>
          <NavLink to="/admin/categories" className={link} data-testid="admin-nav-categories">
            <Tags size={16} /> {t("adminCategories.nav")}
          </NavLink>
          <NavLink to="/admin/events" className={link} data-testid="admin-nav-events">
            <Calendar size={16} /> {t("adminP3.events")}
          </NavLink>
          <NavLink to="/admin/bookings" className={link} data-testid="admin-nav-bookings">
            <ClipboardList size={16} /> {t("adminP3.bookings")}
          </NavLink>
          <NavLink to="/admin/masters" className={link} data-testid="admin-nav-masters">
            <UserCircle2 size={16} /> {t("masters.admin")}
          </NavLink>
          <NavLink to="/admin/articles" className={link} data-testid="admin-nav-articles">
            <BookOpen size={16} /> {t("adminP3.articles")}
          </NavLink>
          <NavLink to="/admin/angelina" className={link} data-testid="admin-nav-angelina">
            <User2 size={16} /> Angelina
          </NavLink>
          <NavLink to="/admin/reports" className={link} data-testid="admin-nav-reports">
            <FileText size={16} /> {t("adminP3.reports")}
          </NavLink>
          <NavLink to="/admin/users" className={link} data-testid="admin-nav-users">
            <Users size={16} /> {t("admin.users")}
          </NavLink>
          <NavLink to="/admin/withdrawals" className={link} data-testid="admin-nav-withdrawals">
            <Wallet size={16} /> {t("adminP2.withdrawals")}
          </NavLink>
          <NavLink to="/admin/org-chart" className={link} data-testid="admin-nav-orgchart">
            <Network size={16} /> {t("adminP2.orgChart")}
          </NavLink>
          <NavLink to="/admin/settings" className={link} data-testid="admin-nav-settings">
            <Settings2 size={16} /> {t("admin.settings")}
          </NavLink>
          <NavLink to="/admin/page-titles" className={link} data-testid="admin-nav-page-titles">
            <FileText size={16} /> 页面标题
          </NavLink>
        </nav>
        <button
          onClick={logout}
          className="mt-auto inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm text-[#4a6e77] hover:text-[#003645] transition-colors duration-200"
          data-testid="admin-logout-btn"
        >
          <LogOut size={16} /> {t("nav.logout")}
        </button>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <div className="md:hidden flex items-center justify-between border-b border-[#e5ebeb] bg-white px-4 py-3">
          <Logo size="sm" />
          <LanguageToggle />
        </div>
        <div className="hidden md:flex items-center justify-end px-8 py-4 border-b border-[#e5ebeb] bg-white/70 backdrop-blur-md">
          <div className="mr-4 text-xs text-[#4a6e77]">{user.email}</div>
          <LanguageToggle />
        </div>
        <div className="flex-1"><Outlet /></div>
      </div>
    </div>
  );
}
